import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useDrop, useDrag } from 'react-dnd';
import { useParams, useNavigate } from 'react-router-dom';

import { useDesign } from '../../contexts/DesignContext';
import { getAIConfigs, getAIResponse } from '../../services/aiApi';
import designService from '../../services/designService';
import AssistantChat from '../aiAssistant/AssistantChat';
import ComponentPalette from '../designTools/ComponentPalette';
import PropertiesPanel from '../designTools/PropertiesPanel';

// Helper function to calculate intersection point of a line with a rectangle
// This function helps to draw connections to component borders
const getIntersectionPoint = (rect, p1, p2) => {
    const { x, y, width, height } = rect;
    const points = [];

    // Check intersection with top side
    if (p1.y < y && p2.y > y || p1.y > y && p2.y < y) {
        const intersectionX = p1.x + (p2.x - p1.x) * (y - p1.y) / (p2.y - p1.y);
        if (intersectionX >= x && intersectionX <= x + width) {
            points.push({ x: intersectionX, y: y });
        }
    }
    // Check intersection with bottom side
    if (p1.y < y + height && p2.y > y + height || p1.y > y + height && p2.y < y + height) {
        const intersectionX = p1.x + (p2.x - p1.x) * (y + height - p1.y) / (p2.y - p1.y);
        if (intersectionX >= x && intersectionX <= x + width) {
            points.push({ x: intersectionX, y: y + height });
        }
    }
    // Check intersection with left side
    if (p1.x < x && p2.x > x || p1.x > x && p2.x < x) {
        const intersectionY = p1.y + (p2.y - p1.y) * (x - p1.x) / (p2.x - p1.x);
        if (intersectionY >= y && intersectionY <= y + height) {
            points.push({ x: x, y: intersectionY });
        }
    }
    // Check intersection with right side
    if (p1.x < x + width && p2.x > x + width || p1.x > x + width && p2.x < x + width) {
        const intersectionY = p1.y + (p2.y - p1.y) * (x + width - p1.x) / (p2.x - p1.x);
        if (intersectionY >= y && intersectionY <= y + height) {
            points.push({ x: x + width, y: intersectionY });
        }
    }

    // Return the point closest to p1 (the source component's center)
    // This prioritizes the more natural connection point
    if (points.length === 0) return null;
    if (points.length === 1) return points[0];

    const dist1 = Math.hypot(points[0].x - p1.x, points[0].y - p1.y);
    const dist2 = Math.hypot(points[1].x - p1.x, points[1].y - p1.y);
    return dist1 < dist2 ? points[0] : points[1];
};


const DesignContainerWrapper = styled.div`
    display: flex;
    height: calc(100vh - 40px); /* Adjust based on your header/footer height */
    overflow: hidden; /* Prevent overall scrollbar */
    margin: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    background-color: #f0f2f5;
`;

const PaletteAndChatContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 300px; /* Initial fixed width for palette/chat area */
    min-width: 250px;
    max-width: 500px;
    resize: horizontal; /* Allow horizontal resizing */
    overflow: auto; /* Scroll if content exceeds height */
    background-color: #ffffff;
    border-right: 1px solid #e0e0e0;
`;

const StyledComponentPalette = styled(ComponentPalette)`
    flex-shrink: 0; /* Don't shrink when resizing */
    padding: 15px;
    border-bottom: 1px solid #e0e0e0;
`;

const StyledAssistantChat = styled(AssistantChat)`
    flex-grow: 1; /* Allow chat to take remaining vertical space */
    padding: 15px;
`;


const CanvasContainer = styled.div`
    flex-grow: 1;
    position: relative;
    overflow: auto;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
	cursor: grab; /* Indicates the canvas is draggable */
`;
   
const Canvas = styled.div`
    min-width: 2000px; /* Significantly increased minimum width for ample space */
    min-height: 1500px; /* Significantly increased minimum height for ample space */
    position: relative;
    border: 1px dashed #ccc;
    background: repeating-linear-gradient(to right, #f9f9f9, #f9f9f9 9px, transparent 9px, transparent 10px),
                repeating-linear-gradient(to bottom, #f9f9f9, #f9f9f9 9px, transparent 9px, transparent 10px);
    background-color: #ffffff;
`;


const DesignArea = styled.div`
    display: flex;
    flex-grow: 1;
	position: relative;
    overflow: hidden;
    background-color: #f0f2f5;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    margin: 20px;
	min-width: 1200px; /* Increased width */
    min-height: 800px; /* Increased height */
`;


   
const Component = styled.div.withConfig({
    shouldForwardProp: (prop) => !['isDragging', 'isSelected', 'isConnecting'].includes(prop)
})`
    position: absolute;
    border: 2px solid ${props => props.isSelected ? '#007bff' : '#333'};
    border-radius: 8px;
    padding: 10px;
    background-color: #f8f9fa;
    /* Cursor changes based on state */
    cursor: ${props => props.isConnecting ? 'crosshair' : 'grab'};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 0.8em;
    font-weight: bold;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease-in-out;
    width: 80px; /* Fixed width for consistency */
    height: 60px; /* Fixed height for consistency */
    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    opacity: ${props => props.isDragging ? 0.5 : 1};
    user-select: none; /* Prevent text selection during drag */

    button {
        margin-top: 5px;
        padding: 3px 8px;
        font-size: 0.7em;
        cursor: pointer;
    }

    svg {
        width: 40px; /* Adjust SVG size to fit within component */
        height: 40px;
        margin-bottom: 5px; /* Space between icon and label */
    }
`;

const ComponentContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ComponentLabel = styled.div`
    position: absolute;
    bottom: 5px; /* Position label below the SVG icon */
    white-space: nowrap;
    font-size: 0.7em;
    font-weight: bold;
	color: #333;
`;

const ConnectionSVG = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none; /* Allows clicks to pass through to components below */
    overflow: visible; /* Ensure arrowheads are not clipped at canvas edges */
`;
   

const ConnectionLine = styled.line`
    stroke: black;
    stroke-width: 2;
    pointer-events: all; /* Make lines clickable if needed */
	&:hover {
        stroke: #007bff;
        stroke-width: 3;
    }
`;

const TemporaryConnection = styled.line`
    stroke: #007bff;
    stroke-width: 2;
    stroke-dasharray: 5, 5;
    pointer-events: none;
`;

const ComponentPopup = styled.div`
    position: absolute;
    background-color: white;
    border: 1px solid #ccc;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    padding: 15px;
    z-index: 1000;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    h4 {
        margin-top: 0;
        margin-bottom: 10px;
		color: #333;
    }
	
	input[type="text"] {
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 0.9em;
    }

    button {
        padding: 8px 12px;
        background-color: #007bff;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        &:hover {
            background-color: #0056b3;
        }
		&.delete-button {
            background-color: #dc3545;
            &:hover {
                background-color: #c82333;
            }
        }
    }
`;


const DashboardButton = styled.button`
    padding: 10px 15px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    margin: 20px;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1001;

    &:hover {
        background-color: #5a6268;
    }
`;


const ItemTypes = {
  COMPONENT: 'component',
  PALETTE_COMPONENT: 'palette_component'
};

// SVG Icons for components (example, will be expanded later)
const RouterIcon = () => (
 <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M32 64C49.6731 64 64 49.6731 64 32C64 14.3269 49.6731 0 32 0C14.3269 0 0 14.3269 0 32C0 49.6731 14.3269 64 32 64Z" fill="#1A78C2"/>
  <path d="M32 40C36.4183 40 40 36.4183 40 32C40 27.5817 36.4183 24 32 24C27.5817 24 24 27.5817 24 32C24 36.4183 27.5817 40 32 40Z" fill="white"/>
  <path d="M32 24V40" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
  <path d="M24 32H40" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
</svg>
);

const SwitchIcon = () => (
 <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="12" y="24" width="40" height="16" rx="4" fill="white"/>
  <rect x="16" y="28" width="4" height="4" fill="#1A78C2"/>
  <rect x="24" y="28" width="4" height="4" fill="#1A78C2"/>
  <rect x="32" y="28" width="4" height="4" fill="#1A78C2"/>
  <rect x="40" y="28" width="4" height="4" fill="#1A78C2"/>

  <rect x="16" y="34" width="4" height="4" fill="#1A78C2"/>
  <rect x="24" y="34" width="4" height="4" fill="#1A78C2"/>
  <rect x="32" y="34" width="4" height="4" fill="#1A78C2"/>
  <rect x="40" y="34" width="4" height="4" fill="#1A78C2"/>
</svg>
);

const FirewallIcon = () => (
 <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <rect x="20" y="20" width="10" height="8" fill="#1A78C2"/>
  <rect x="34" y="20" width="10" height="8" fill="#1A78C2"/>
  <rect x="20" y="30" width="10" height="8" fill="#1A78C2"/>
  <rect x="34" y="30" width="10" height="8" fill="#1A78C2"/>
  <rect x="20" y="40" width="10" height="8" fill="#1A78C2"/>
  <rect x="34" y="40" width="10" height="8" fill="#1A78C2"/>
</svg>
);

const ServerIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <rect x="20" y="22" width="24" height="6" rx="2" fill="#1A78C2"/>
  <rect x="20" y="30" width="24" height="6" rx="2" fill="#1A78C2"/>
  <rect x="20" y="38" width="24" height="6" rx="2" fill="#1A78C2"/>
</svg>
);

const FCSwitchIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <path d="M20 24L44 40" stroke="#1A78C2" stroke-width="4" stroke-linecap="round"/>
  <path d="M44 24L20 40" stroke="#1A78C2" stroke-width="4" stroke-linecap="round"/>
  <circle cx="20" cy="24" r="2" fill="#1A78C2"/>
  <circle cx="44" cy="40" r="2" fill="#1A78C2"/>
  <circle cx="44" cy="24" r="2" fill="#1A78C2"/>
  <circle cx="20" cy="40" r="2" fill="#1A78C2"/>
</svg>
);

const NacSystemIcon = () => (
 <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <path d="M32 20L20 28V36C20 42.6 25.12 48 32 48C38.88 48 44 42.6 44 36V28L32 20Z" fill="#1A78C2"/>
  <path d="M24 24L20 20" stroke="white" stroke-width="2" stroke-linecap="round"/>
  <path d="M40 24L44 20" stroke="white" stroke-width="2" stroke-linecap="round"/>
  <path d="M24 40L20 44" stroke="white" stroke-width="2" stroke-linecap="round"/>
  <path d="M40 40L44 44" stroke="white" stroke-width="2" stroke-linecap="round"/>
</svg>
);

const StorageDeviceIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <rect x="20" y="20" width="24" height="8" rx="2" fill="#1A78C2"/>
  <rect x="20" y="30" width="24" height="8" rx="2" fill="#1A78C2"/>
  <rect x="20" y="40" width="24" height="8" rx="2" fill="#1A78C2"/>
</svg>
);


const LoadBalancerIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#1A78C2"/>
  <rect x="16" y="16" width="32" height="32" rx="8" fill="white"/>
  <path d="M20 32H44" stroke="#1A78C2" stroke-width="4" stroke-linecap="round"/>
  <path d="M38 26L44 32L38 38" stroke="#1A78C2" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M28 32C28 30 28 28 30 26" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
  <path d="M28 32C28 34 28 36 30 38" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
</svg>
);


const ConnectionItemIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);


const PcHostIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="10" y="10" width="44" height="30" rx="3" fill="#1A78C2"/>
  <rect x="12" y="12" width="40" height="26" rx="2" fill="white"/>
  <rect x="28" y="40" width="8" height="6" fill="#1A78C2"/>
  <rect x="22" y="46" width="20" height="4" rx="1" fill="#1A78C2"/>
  <rect x="18" y="52" width="28" height="3" rx="0.5" fill="#1A78C2"/>
</svg>
);

const MobileDeviceIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="20" y="5" width="24" height="54" rx="5" fill="#1A78C2"/>
  <rect x="22" y="8" width="20" height="48" rx="3" fill="white"/>
  <circle cx="32" cy="53" r="1.5" fill="#1A78C2"/>
</svg>
);

const IoTDeviceIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="20" y="20" width="24" height="24" rx="4" fill="#1A78C2"/>
  <rect x="23" y="23" width="18" height="18" rx="2" fill="white"/>
  <path d="M32 18C37.5228 18 42 22.4772 42 28" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
  <path d="M32 14C39.732 14 46 20.268 46 28" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
  <path d="M32 10C41.9806 10 50 18.0194 50 28" stroke="#1A78C2" stroke-width="2" stroke-linecap="round"/>
</svg>
);

const PrinterIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="12" y="20" width="40" height="28" rx="3" fill="#1A78C2"/>
  <path d="M15 20L15 15L48 15L48 20L15 20Z" fill="white"/>
  <rect x="15" y="44" width="34" height="4" fill="white"/>
  <rect x="42" y="23" width="7" height="4" rx="1" fill="white"/>
</svg>
);

const IpPhoneIcon = () => (
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="15" y="25" width="34" height="24" rx="3" fill="#1A78C2"/>
  <rect x="18" y="28" width="12" height="18" rx="2" fill="white"/>
  <path d="M35 15C35 10 30 10 30 10L25 10C25 10 20 10 20 15L20 20C20 25 25 25 25 25L30 25C30 25 35 25 35 20V15Z" fill="#1A78C2"/>
  <rect x="22" y="12" width="11" height="2" fill="white"/>
  <rect x="22" y="20" width="11" height="2" fill="white"/>
</svg>
);

// Mapping component types to their SVG icons
const componentIcons = {
    router: RouterIcon,
    switch: SwitchIcon,
    firewall: FirewallIcon,
    server: ServerIcon,
    fcswitch: FCSwitchIcon,
    nacsystem: NacSystemIcon,
    storagedevice: StorageDeviceIcon,
    load_balancer: LoadBalancerIcon, 
	pchost: PcHostIcon,
	mobiledevice: MobileDeviceIcon,
	iotdevice: IoTDeviceIcon,
	printer: PrinterIcon,
	ipphone: IpPhoneIcon,
};


const DraggableComponent = ({ id, type, label, x_coordinate, y_coordinate, onMouseDown, onConnectEnd, onContextMenu, onClick, isSelected, isConnecting, isDragging }) => {
    
  // Determine which icon to render based on the component type
  const Icon = componentIcons[type?.toLowerCase()] || ServerIcon; // Default to ServerIcon if type not found
  	

  return (
        <Component
            style={{ left: `${x_coordinate}px`, top: `${y_coordinate}px` }}
            onMouseDown={(event) => onMouseDown && onMouseDown(event, id)}
            onClick={(event) => onClick(event, id, x_coordinate, y_coordinate)}
            onMouseUp={() => onConnectEnd(id)} // Mouse up anywhere on component ends connection attempt
            onContextMenu={onContextMenu}
            isSelected={isSelected}
			isConnecting={isConnecting}
            isDragging={isDragging}
        >
            <ComponentContent>
                <Icon /> {/* Render the SVG icon */}
                <ComponentLabel>{label || type}</ComponentLabel>
            </ComponentContent>
        </Component>
    );
};


function DesignCanvas() {
  const { design, setDesign } = useDesign();
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [error, setError] = useState(null);
  const { designId } = useParams();
  
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStartComponentId, setConnectionStartComponentId] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const [aiResponse, setAIResponse] = useState(null);
  const [aiError, setAiError] = useState(null);
  
  const [isLoadingAI, setIsLoadingAI] = useState(false); // Track loading state
  const [showPopup, setShowPopup] = useState(false);
  const [popupComponent, setPopupComponent] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  	
  const canvasRef = useRef(null); // Ref for the canvas element
  const popupRef = useRef(null);
  
  const navigate = useNavigate();
  
  // State for managing canvas pan/zoom if implemented, or just scroll position
  const [canvasScroll, setCanvasScroll] = useState({ x: 0, y: 0 });
  
  const [draggingComponentId, setDraggingComponentId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Ref for the currently dragging component's actual DOM element
  const draggingComponentRef = useRef(null);
  const [editProperties, setEditProperties] = useState(false);
  
    // --- Drag and Drop Logic ---
    const [, drop] = useDrop(() => ({
        accept: [ItemTypes.PALETTE_COMPONENT],
        drop: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();
            if (!canvasRef.current || !clientOffset) return;

            const canvasRect = canvasRef.current.getBoundingClientRect();

            // Calculate position relative to the scrollable canvas content, centering the dropped component
            const new_x_coordinate = Math.max( 0, clientOffset.x - canvasRect.left + canvasRef.current.scrollLeft - (80 / 2));
            const new_y_coordinate = Math.max(0, clientOffset.y - canvasRect.top + canvasRef.current.scrollTop - (60 / 2));

            if (item.componentType) {
                handleAddComponent(
                    item.componentType,
                    new_x_coordinate,
                    new_y_coordinate,
                    `${item.componentType}`,
                    {}
                );
            } else if (item.type === ItemTypes.COMPONENT) {
                // This `drop` function mainly confirms the drop for react-dnd.
                // We ensure the actual position is updated only once on mouseUp.
                const component = design.components.find(c => c.id === item.id);
                if (component) {
                     // Ensure the final position is set here
                    handleUpdateComponent(item.id, {
                        x_coordinate: new_x_coordinate,
                        y_coordinate: new_y_coordinate,
                    });
                }
            }
            return undefined; // Indicate that drop was handled
        },
    }));
  
  // Fetch design data when designId changes
    useEffect(() => {
        // Only fetch design if we are in a design-related mode and have a designId
        if (designId) {
            const fetchDesignData = async () => {
                try {
                    const fetchedDesign = await designService.fetchDesign(designId);
                    setDesign(fetchedDesign);
                    setError(null); // Clear previous errors
                } catch (err) {
                    console.error("Failed to fetch design:", err);
                    setError("Failed to load design data. It might not exist or you don't have access.");
                }
            };
            fetchDesignData();
        } else {
            setError("No design ID provided in the URL.");
        }
    }, [designId, setDesign]); // Added currentMode to dependency array
  

  const handleAddComponent = useCallback(async (type, x_coordinate, y_coordinate, label, properties = {}) => {
	  
		if (!designId) {
            setError("No design selected. Cannot add component.");
            return;
        }

        try {  
            const newComponent = await designService.addComponentToDesign(
                designId,
                type,
                x_coordinate,
                y_coordinate,
                label,
                properties 
            );

            setDesign(prevDesign => ({
                ...prevDesign,
                components: [...(prevDesign?.components || []), newComponent]
            }));
        } catch (error) {
            console.error('Error adding component:', error);
            setError('Failed to add component. Please try again.');
        }
    }, [designId, setDesign]);
  
  
  const handleUpdateComponent = useCallback(async (id, updatedData) => {
	  
		if (!designId) {
            setError("No design selected. Cannot update component.");
            return;
        }

        try {
            const updated = await designService.updateComponentInDesign(designId, id, updatedData);

            setDesign(prevDesign => ({
                ...prevDesign,
                components: prevDesign.components.map(c =>
                    c.id === updated.id ? { ...c, ...updated } : c
                )
            }));
        } catch (error) {
            console.error('Error updating component:', error);
            setError('Failed to update component.');
        }
    }, [designId, setDesign]);
	
    // This handles both simple click (for drag) and right-click (for popup)
    const handleComponentClick = useCallback((event, id, componentX, componentY) => {
        event.stopPropagation(); // Prevent canvas click from deselecting/closing popup

        if (event.button === 2) { // Right-click (context menu)
            event.preventDefault(); // Prevent default browser context menu
            const component = design.components.find(c => c.id === id);
            if (component) {
                // Calculate popup position near the component, but ensure it's within viewport
                const componentDom = event.currentTarget.getBoundingClientRect();
                const canvasDom = canvasRef.current.getBoundingClientRect();
				const canvasHeight = canvasRef.current.clientHeight;

                let popupX = componentDom.left - canvasDom.left + (componentDom.width / 2);
                let popupYBelow = componentDom.top - canvasDom.top + componentDom.height + 10; // 10px below component

				const estimatedPopupHeight = 200;
				
				let finalPopupY = popupYBelow;
				
				// Check if there's enough space below the component for the popup
				const spaceBelowComponent = canvasHeight - (componentDom.top - canvasDom.top + componentDom.height);
				
				if (spaceBelowComponent < estimatedPopupHeight) {
                // Not enough space below, position above the component
                finalPopupY = componentDom.top - canvasDom.top - estimatedPopupHeight - 10; // 10px above component
            }
				
                // Adjust for canvas scroll to get absolute position within the canvas content
                popupX += canvasRef.current.scrollLeft;
                finalPopupY += canvasRef.current.scrollTop;

                setPopupComponent(component);
                setPopupPosition({ x: popupX, y: finalPopupY });
                setShowPopup(true);
				setSelectedComponentId(id); // Select component when right-clicked
            }
        } else if (event.button === 0) { // Left-click
            if (showPopup) {
                handleCloseComponentPopup(); // Close popup if open
            }
            setSelectedComponentId(id); // Select the component
            // Drag logic starts in handleComponentMouseDown
        }
    }, [design.components, showPopup]);

  const handleEditProperties = (id) => {
    setSelectedComponentId(id);
	setEditProperties(true);
	
  };	
	
    const handleCloseComponentPopup = useCallback(() => {
        setShowPopup(false);
        setPopupComponent(null);
    }, []);	
	
	
    // Effect to close popup if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target) &&
                canvasRef.current && !canvasRef.current.contains(event.target)) {
                handleCloseComponentPopup();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleCloseComponentPopup]);	
  
  
  const handleConnectStart = useCallback((startComponentId) => {
        setIsConnecting(true);
        setConnectionStartComponentId(startComponentId);
        setSelectedComponentId(null);
        handleCloseComponentPopup();
    }, [handleCloseComponentPopup]);	
	
    // Handle canvas scroll for proper mouse coordinates
    useEffect(() => {
        const canvas = canvasRef.current;
        const handleScroll = () => {
            setCanvasScroll({ x: canvas.scrollLeft, y: canvas.scrollTop });
        };
        if (canvas) {
            canvas.addEventListener('scroll', handleScroll);
            return () => canvas.removeEventListener('scroll', handleScroll);
        }
    }, []);	

    const handleCanvasMouseMove = useCallback((event) => {
        if (!canvasRef.current) return;

        const canvasRect = canvasRef.current.getBoundingClientRect();
        // Adjust mouse position by canvas scroll
        const mouseX = event.clientX - canvasRect.left + canvasRef.current.scrollLeft;
        const mouseY = event.clientY - canvasRect.top + canvasRef.current.scrollTop;

        if (isConnecting) {
            setMousePosition({ x: mouseX, y: mouseY });
        } else if (draggingComponentId) {
        // Translation accounting for initial grab offset, scroll, and boundaries without jitter
        const newX = Math.max(0, mouseX - dragOffset.x);
        const newY = Math.max(0, mouseY - dragOffset.y);

        // Immediate visual state update (no network call during move)
        setDesign(prevDesign => {
            if (!prevDesign?.components) return prevDesign;
            return {
                ...prevDesign,
                components: prevDesign.components.map(c =>
                    c.id === draggingComponentId
                        ? { ...c, x_coordinate: newX, y_coordinate: newY }
                        : c
                )
            };
        });
    }
}, [isConnecting, draggingComponentId, dragOffset, setDesign]);

    // This is the actual drag start for components
    const handleComponentMouseDown = useCallback((event, id) => {
    if (event.button !== 0 || isConnecting) return; // Left click only and not in connecting mode
    event.stopPropagation();

    if (showPopup) {
        handleCloseComponentPopup();
    }

    setSelectedComponentId(id);

    const component = design?.components?.find(c => c.id === id);
    if (!component || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseCanvasX = event.clientX - canvasRect.left + canvasRef.current.scrollLeft;
    const mouseCanvasY = event.clientY - canvasRect.top + canvasRef.current.scrollTop;

    // Track the delta offset from the cursor to component top-left
    setDragOffset({
        x: mouseCanvasX - component.x_coordinate,
        y: mouseCanvasY - component.y_coordinate,
    });
    setDraggingComponentId(id);
}, [isConnecting, showPopup, handleCloseComponentPopup, design?.components]);


    // New handler for mouse up on the canvas (or anywhere, to end drag)
    const handleCanvasMouseUp = useCallback(async (event) => {
    if (isConnecting) {
        setIsConnecting(false);
        setConnectionStartComponentId(null);
        setMousePosition({ x: 0, y: 0 });
    }

    if (draggingComponentId) {
        const componentToUpdate = design?.components?.find(c => c.id === draggingComponentId);
        if (componentToUpdate) {
            // Single-commit backend update upon drag completion
            await handleUpdateComponent(draggingComponentId, {
                x_coordinate: componentToUpdate.x_coordinate,
                y_coordinate: componentToUpdate.y_coordinate,
            });
        }
        setDraggingComponentId(null);
    }
}, [isConnecting, draggingComponentId, design?.components, handleUpdateComponent]);

    const handleComponentMouseUp = async (endComponentId) => {
        if (isConnecting && connectionStartComponentId && endComponentId !== connectionStartComponentId) {
            try {
                const componentWidth = 80;
                const componentHeight = 60;

                const sourceComponent = design?.components.find(c => c.id === connectionStartComponentId);
                const targetComponent = design?.components.find(c => c.id === endComponentId);

                if (sourceComponent && targetComponent) {
                    const startRect = { x: sourceComponent.x_coordinate, y: sourceComponent.y_coordinate, width: componentWidth, height: componentHeight };
                    const endRect = { x: targetComponent.x_coordinate, y: targetComponent.y_coordinate, width: componentWidth, height: componentHeight };

                    const lineStart = { x: sourceComponent.x_coordinate + componentWidth / 2, y: sourceComponent.y_coordinate + componentHeight / 2 };
                    const lineEnd = { x: targetComponent.x_coordinate + componentWidth / 2, y: targetComponent.y_coordinate + componentHeight / 2 };

                    const intersectionStart = getIntersectionPoint(startRect, lineStart, lineEnd) || lineStart;
                    const intersectionEnd = getIntersectionPoint(endRect, lineStart, lineEnd) || lineEnd;

                    const connectionConfig = {
                        startX: intersectionStart.x,
                        startY: intersectionStart.y,
                        endX: intersectionEnd.x,
                        endY: intersectionEnd.y,
                    };
					

                    const newConnection = await designService.connectComponentsInDesign(
                        designId,
                        connectionStartComponentId,
                        endComponentId,
                        { config: connectionConfig }
                    );
                    setDesign(prevDesign => ({ ...prevDesign, connections: [...(prevDesign?.connections || []), newConnection] }));
					
					console.log(newConnection);
                }
            } catch (error) {
                console.error('Error connecting components:', error);
                setError('Failed to connect components.');
            } finally {
                setIsConnecting(false);
                setConnectionStartComponentId(null);
                setMousePosition({ x: 0, y: 0 });
            }
        } else {
            setIsConnecting(false);
            setConnectionStartComponentId(null);
            setMousePosition({ x: 0, y: 0 });
        }
    };

    useEffect(() => {
    if (!draggingComponentId) return;

    const handleGlobalMouseUp = async () => {
        const componentToUpdate = design?.components?.find(c => c.id === draggingComponentId);
        if (componentToUpdate) {
            await handleUpdateComponent(draggingComponentId, {
                x_coordinate: componentToUpdate.x_coordinate,
                y_coordinate: componentToUpdate.y_coordinate,
            });
        }
        setDraggingComponentId(null);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
}, [draggingComponentId, design?.components, handleUpdateComponent]);
	
  const handleRemoveComponent = useCallback(async (id) => {
	  
		if (!designId) {
            setError("No design selected. Cannot remove component.");
            return;
        }
	  
        try {
            await designService.deleteComponentFromDesign(designId, id);
            setDesign(prevDesign => {
                const updatedComponents = prevDesign.components.filter(c => c.id !== id);
                const updatedConnections = prevDesign.connections.filter(conn =>
                    conn.source_component !== id && conn.target_component !== id
                );
                return {
                    ...prevDesign,
                    components: updatedComponents,
                    connections: prevDesign.connections.filter(conn =>
                        conn.source_component !== id && conn.target_component !== id
                    )
                };
            });
            setSelectedComponentId(null); // Deselect if removed
            handleCloseComponentPopup();
        } catch (error) {
            console.error('Error removing component:', error);
            setError('Failed to remove component.');
        }
    }, [designId, setDesign, handleCloseComponentPopup]);
	
    const handleShowComponentPopup = useCallback((component, event) => {
        event.preventDefault();
        setPopupComponent(component);
        setPopupPosition({ x: event.clientX, y: event.clientY });
        setShowPopup(true);
    }, []);
	
	
    const handleConnectOptionClick = useCallback((sourceComponentId) => {
        handleCloseComponentPopup();
        handleConnectStart(sourceComponentId);
    }, [handleConnectStart, handleCloseComponentPopup]);
		
	
    const handleDeleteConnectionClick = useCallback(async (id) => {
        if ((!designId)) {
            setError("No design selected. Cannot remove connection.");
            return;
        }

        try {
            await designService.deleteConnectionFromDesign(designId, id);
            setDesign(prevDesign => ({
                ...prevDesign,
                connections: prevDesign.connections.filter(conn => conn.id !== id)
			}));

            console.log(`Connection  deleted successfully.`);
        } catch (error) {
            console.error('Failed to delete connection:', error);
            alert(`Failed to delete connection: ${error.message || error}`);
        } 
    }, [designId, setDesign]);

	
    const handleDashboardRedirect = useCallback(() => {
        navigate('/dashboard');
    }, [navigate]);
	 
	 // --- AI Integration ---

    // This function prepares the design data in a format consumable by the AI assistant.
    const getDesignDataForAI = useCallback(() => {
        if (!design) return null;

        const ai_components = design.components.map(comp => ({
            id: comp.id,
            type: comp.type,
            label: comp.label,
            x_coordinate: comp.x_coordinate,
            y_coordinate: comp.y_coordinate,
            properties: comp.properties || {}, // Ensure properties is an object
        }));

        const ai_connections = design.connections.map(conn => ({
            id: conn.id,
            // Backend now uses source_component and target_component fields,
            source_component_id: conn.source_component,
            target_component_id: conn.target_component,
            bandwidth: conn.bandwidth,
            protocol: conn.protocol,
            connection_type: conn.connection_type,
            latency: conn.latency,
            notes: conn.notes,
            config: conn.config || {}, // Ensure config is an object (for line coords etc.)
        }));

        return {
            design_id: design.id,
            name: design.name,
            description: design.description,
            components: ai_components,
            connections: ai_connections
        };
    }, [design]);
	
	// This function applies AI suggestions back to the current design state.
    const applyUpdatesToDesign = useCallback((updatedDesign) => {

        if (!updatedDesign) {
            console.warn("AI returned no updated design data.");
            return;
        }

        setDesign(prevDesign => {
            let newComponents = prevDesign.components || [];
            if (updatedDesign.components) {
                const aiComponentMap = new Map(updatedDesign.components.map(c => [c.id, c]));

                // Update existing components and add new ones from AI
                newComponents = prevDesign.components.map(existingComp => {
                    const aiComp = aiComponentMap.get(existingComp.id);
                    if (aiComp) {
                        // Merge existing data with AI data. Be careful with deep merges for properties/config.
                        // For coordinates, ensure AI's x_coordinate/y_coordinate map back to 'position'.
                        return {
                            ...existingComp,
                            ...aiComp,
                            position: aiComp.x_coordinate !== undefined && aiComp.y_coordinate !== undefined
                                ? { x: aiComp.x_coordinate, y: aiComp.y_coordinate }
                                : existingComp.position, // Preserve current position if AI doesn't change it
                            properties: { ...(existingComp.properties || {}), ...(aiComp.properties || {}) }
                        };
                    }
                    return existingComp; // Keep components not touched by AI
                }).concat(
                    // Add components that are entirely new from AI
                    updatedDesign.components.filter(aiComp => !prevDesign.components.some(c => c.id === aiComp.id))
                    .map(newAiComp => ({
                        ...newAiComp,
                        position: { x: newAiComp.x_coordinate, y: newAiComp.y_coordinate } // Map AI coords to local position
                    }))
                );

                // If AI provides a definitive *full list*, you might want to remove components not in AI's list:
                // const finalAiComponentIds = new Set(updatedDesign.components.map(comp => comp.id));
                // newComponents = newComponents.filter(comp => finalAiComponentIds.has(comp.id));
            }

            let newConnections = prevDesign.connections || [];
            if (updatedDesign.connections) {
                const aiConnectionMap = new Map(updatedDesign.connections.map(c => [c.id, c]));

                newConnections = prevDesign.connections.map(existingConn => {
                    const aiConn = aiConnectionMap.get(existingConn.id);
                    if (aiConn) {
                        // Map AI's source/target IDs back to 'from'/'to'
                        return {
                            ...existingConn,
                            ...aiConn,
                            from: aiConn.source_component_id,
                            to: aiConn.target_component_id,
                            config: { ...(existingConn.config || {}), ...(aiConn.config || {}) }
                        };
                    }
                    return existingConn;
                }).concat(
                    // Add new connections from AI
                    updatedDesign.connections.filter(aiConn => !prevDesign.connections.some(c => c.id === aiConn.id))
                    .map(newAiConn => ({
                        ...newAiConn,
                        from: newAiConn.source_component_id,
                        to: newAiConn.target_component_id
                    }))
                );

                // Similar removal logic for connections if AI sends a definitive full list
                // const finalAiConnectionIds = new Set(updatedDesign.connections.map(conn => conn.id));
                // newConnections = newConnections.filter(conn => finalAiConnectionIds.has(conn.id));
            }

            // Update top-level design properties if provided by AI
            return {
                ...prevDesign,
                components: newComponents,
                connections: newConnections,
                name: updatedDesign.name || prevDesign.name,
                description: updatedDesign.description || prevDesign.description,
            };
        });
        setAiError(null); // Clear any AI-related errors upon successful update
    }, [setDesign]);


    // This is the primary AI message handler called by AssistantChat
    const handleAssistantMessage = async (message) => {
        setIsLoadingAI(true); // Indicate AI processing is starting
        setAiError(null);     // Clear any previous AI errors
        let aiResponseText = "I'm processing your request..."; // Initial response for chat UI

        try {
            const currentDesignDataForAI = getDesignDataForAI();

            // --- Basic Intent Recognition (can be greatly expanded on the backend) ---
            const lowerCaseMessage = message.toLowerCase();

            if (lowerCaseMessage.includes('add') || lowerCaseMessage.includes('remove') ||
                lowerCaseMessage.includes('create') || lowerCaseMessage.includes('delete') ||
                lowerCaseMessage.includes('update design') || lowerCaseMessage.includes('modify design') || lowerCaseMessage.includes('improve')) {
                // Intent: Design Modification
                console.log("AI Intent: Design Modification Request");
                //const aiSuggestedData = await designService.applyAISuggestionsToDesign(designId, {
                //    user_message: message, // Send user's original message for AI context
                //    current_design: currentDesignDataForAI // Send current design state
                //});
				
				const aiResponse = await getAIResponse(message, currentDesignDataForAI, designId);
				
                const reFetchDesignDataForAi = await designService.fetchDesign(designId);
				
                applyUpdatesToDesign(reFetchDesignDataForAi); // Apply the changes to the frontend state
                //aiResponseText = "I've updated the design based on your request!";
				
				
                aiResponseText = aiResponse || "Design updated...";

            } else if (lowerCaseMessage.includes('configuration') || lowerCaseMessage.includes('configs') ||
                        lowerCaseMessage.includes('settings') ||
					   lowerCaseMessage.includes('command') || lowerCaseMessage.includes('commands')) {
                // Intent: Get Configurations/Properties for existing design components/connections
                console.log("AI Intent: Request for Configurations/Properties");
                const aiResponse = await getAIConfigs(message, currentDesignDataForAI, designId); // Pass current design for context
                aiResponseText = aiResponse; // Assuming getAIConfigs returns the text directly

            } else {
                // Intent: General Chat / Q&A about the design
                console.log("AI Intent: General Chat Request");
                const aiResponse = await getAIResponse(message, currentDesignDataForAI, designId); // Pass current design for context
                aiResponseText = aiResponse; // Assuming getAIResponse returns the text directly
				const reFetchDesignDataForAi = await designService.fetchDesign(designId);
                applyUpdatesToDesign(reFetchDesignDataForAi);
            }

        } catch (error) {
            console.error('Error interacting with AI assistant:', error);
            // Set an error message that can be displayed in the chat UI
            setAiError('Failed to get AI response or apply changes. Please try again.');
            aiResponseText = "An error occurred while processing your request. Please try again!.";
        } finally {
            setIsLoadingAI(false); // Always reset loading state
            return aiResponseText; // Return the determined AI text response to AssistantChat
        }
    };
	
	
    // Keyboard navigation for selected component
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!selectedComponentId || !design?.components || (editProperties === true)) return;

            const component = design.components.find(c => c.id === selectedComponentId);
            if (!component) return;

            let newX = component.x_coordinate;
            let newY = component.y_coordinate;
            const moveAmount = 10; // Pixels to move per key press

            switch (event.key) {
                case 'ArrowLeft':
                    newX -= moveAmount;
                    event.preventDefault(); // Prevent default scroll behavior
                    break;
                case 'ArrowRight':
                    newX += moveAmount;
                    event.preventDefault();
                    break;
                case 'ArrowUp':
                    newY -= moveAmount;
                    event.preventDefault();
                    break;
                case 'ArrowDown':
                    newY += moveAmount;
                    event.preventDefault();
                    break;
                default:
                    return; // Do nothing for other keys
            }

            // Update component position immediately for visual feedback
            setDesign(prevDesign => ({
                ...prevDesign,
                components: prevDesign.components.map(c =>
                    c.id === selectedComponentId
                        ? { ...c, x_coordinate: newX, y_coordinate: newY }
                        : c
                )
            }));

            // Debounce the API call to update the backend
            // In a real application, you'd want to debounce this heavily to avoid too many API calls
            // For now, we'll call it directly for demonstration, but be aware of performance implications
            // A more robust solution would involve a useCallback with a debounce function.
            handleUpdateComponent(selectedComponentId, { x_coordinate: newX, y_coordinate: newY });
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedComponentId, design?.components, handleUpdateComponent, setDesign]); // Add setDesign to deps for direct state update

	if (!design) {
        return <div>Loading design...{error && <p style={{ color: 'red' }}>{error}</p>}</div>;
    }
	
	const selectedComponent = design.components.find(c => c.id === selectedComponentId);

    return (
        <DesignContainerWrapper>
            <DashboardButton onClick={handleDashboardRedirect}>Go to Dashboard</DashboardButton>
            <PaletteAndChatContainer>
                <StyledComponentPalette onAddComponent={handleAddComponent} />
                <AssistantChat
                    onSendMessage={handleAssistantMessage}
                    isLoading={isLoadingAI}
                    error={aiError}
                />
            </PaletteAndChatContainer>

            <CanvasContainer ref={canvasRef}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseDown={() => {
                    // Deselect component and close popup if clicking on canvas background
                    setSelectedComponentId(null);
                    handleCloseComponentPopup();
                }}
            >
                <Canvas ref={drop} onContextMenu={(e) => e.preventDefault()}>
                    {design.components && design.components.map(component => {
                        const displayX = component.x_coordinate;
                        const displayY = component.y_coordinate;

                        return (
                            <DraggableComponent
                                key={component.id}
                                id={component.id}
                                type={component.type}
                                label={component.label}
                                x_coordinate={displayX}
                                y_coordinate={displayY}
                                properties={component.properties}
                                onMouseDown={handleComponentMouseDown}
                                onClick={(e) => handleComponentClick(e, component.id, displayX, displayY)}
                                onConnectEnd={handleComponentMouseUp}
                                onContextMenu={(e) => handleComponentClick(e, component.id, displayX, displayY)} // Use click handler for consistency
                                isSelected={selectedComponentId === component.id}
                                isConnecting={isConnecting && connectionStartComponentId === component.id}
                                isDragging={draggingComponentId === component.id}
                            />
                        );
                    })}

                    <ConnectionSVG>
                        {design.connections && design.connections.map(connection => {
                            const sourceComponent = design.components.find(c => c.id === connection.source_component);
                            const targetComponent = design.components.find(c => c.id === connection.target_component);

                            if (!sourceComponent || !targetComponent) return null;

                            const componentWidth = 80;
                            const componentHeight = 60;

                            const sourceCenter = {
                                x: sourceComponent.x_coordinate + componentWidth / 2,
                                y: sourceComponent.y_coordinate + componentHeight / 2
                            };
                            const targetCenter = {
                                x: targetComponent.x_coordinate + componentWidth / 2,
                                y: targetComponent.y_coordinate + componentHeight / 2
                            };

                            const sourceRect = {
                                x: sourceComponent.x_coordinate,
                                y: sourceComponent.y_coordinate,
                                width: componentWidth,
                                height: componentHeight
                            };
                            const targetRect = {
                                x: targetComponent.x_coordinate,
                                y: targetComponent.y_coordinate,
                                width: componentWidth,
                                height: componentHeight
                            };

                            const startPoint = getIntersectionPoint(sourceRect, sourceCenter, targetCenter) || sourceCenter;
                            const endPoint = getIntersectionPoint(targetRect, sourceCenter, targetCenter) || targetCenter;

                            return (
                                <ConnectionLine
                                    key={connection.id}
                                    x1={startPoint.x}
                                    y1={startPoint.y}
                                    x2={endPoint.x}
                                    y2={endPoint.y}
                                    onClick={() => handleDeleteConnectionClick(connection.id)}
                                />
                            );
                        })}
                        {isConnecting && connectionStartComponentId && (
                            <TemporaryConnection
                                x1={design.components.find(c => c.id === connectionStartComponentId).x_coordinate + 80 / 2}
                                y1={design.components.find(c => c.id === connectionStartComponentId).y_coordinate + 60 / 2}
                                x2={mousePosition.x}
                                y2={mousePosition.y}
                            />
                        )}
                    </ConnectionSVG>
                </Canvas>
            </CanvasContainer>

            {showPopup && popupComponent && (
                <ComponentPopup
                    ref={popupRef}
                    style={{
                        left: popupPosition.x,
                        top: popupPosition.y,
                        // Ensure popup stays within viewport
                        transform: `translate(-50%, 0%)`, // Center horizontally
                        // Basic boundary check (more advanced needed for full edge cases)
                        // This might need further refinement based on the exact size of your popup
                        // and canvas boundaries to prevent it from going off-screen.
                        // For now, it tries to center above the component.
                    }}
                >
                    <h4>Edit Component</h4>
                    <label htmlFor="componentLabel">Label:</label>
                    <input
                        type="text"
                        id="componentLabel"
                        value={popupComponent.label || ''}
                        onChange={(e) => setPopupComponent(prev => ({ ...prev, label: e.target.value }))}
                        onKeyDown={(e) => {
                            // Prevent key events from propagating to canvas for movement
                            e.stopPropagation();
                        }}
                    />
                    {/* Add more properties here as needed */}
                    <button onClick={() => {
                        handleUpdateComponent(popupComponent.id, { label: popupComponent.label });
                        handleCloseComponentPopup();
                    }}>Update</button>
					<button onClick={() => handleEditProperties(popupComponent.id)}>Edit Properties</button>
                    <button onClick={() => handleConnectOptionClick(popupComponent.id)}>Connect</button>
                    <button className="delete-button" onClick={() => handleRemoveComponent(popupComponent.id)}>Delete</button>
                    <button onClick={() => {
						handleCloseComponentPopup();
						setEditProperties(false);
					}}>Close</button>
                </ComponentPopup>
            )}
			

            {selectedComponent && editProperties && (
                <PropertiesPanel
                    selectedComponentId={selectedComponentId}
					designId={designId}
                    onUpdateComponent={handleUpdateComponent}
                    onRemoveComponent={handleRemoveComponent}
                    onConnectStart={handleConnectStart}
                    onClose={() => {
						setSelectedComponentId(null);
						setEditProperties(false);
						}}
                />
            )}
			
        </DesignContainerWrapper>
    );
}

export default DesignCanvas;